import { useEffect, useRef } from 'react'

interface Star {
  x: number
  y: number
  z: number
  size: number
  opacity: number
  color: string
}

export default function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mouseRef = useRef({ x: 0, y: 0 })
  const starsRef = useRef<Star[]>([])
  const animationRef = useRef<number>()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Initialize stars
    const starColors = [
      'rgba(100, 180, 255, ',  // Blue
      'rgba(150, 120, 255, ',  // Purple
      'rgba(80, 200, 255, ',   // Cyan
      'rgba(200, 150, 255, ',  // Lavender
      'rgba(255, 255, 255, ',  // White
    ]

    const initStars = () => {
      starsRef.current = []
      const numStars = Math.floor((canvas.width * canvas.height) / 3000)

      for (let i = 0; i < numStars; i++) {
        starsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          z: Math.random() * 1000,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.8 + 0.2,
          color: starColors[Math.floor(Math.random() * starColors.length)]
        })
      }
    }
    initStars()

    // Mouse tracking
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener('mousemove', handleMouseMove)

    // Animation loop
    const animate = () => {
      ctx.fillStyle = 'rgba(8, 12, 24, 1)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw subtle gradient overlay
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, canvas.width * 0.8
      )
      gradient.addColorStop(0, 'rgba(20, 40, 80, 0.3)')
      gradient.addColorStop(0.5, 'rgba(15, 25, 50, 0.2)')
      gradient.addColorStop(1, 'rgba(8, 12, 24, 0)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const mouseX = mouseRef.current.x
      const mouseY = mouseRef.current.y

      starsRef.current.forEach((star) => {
        // Calculate distance from mouse
        const dx = mouseX - star.x
        const dy = mouseY - star.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const maxDistance = 300

        // Stars are attracted to mouse
        if (distance < maxDistance && distance > 0) {
          const force = (maxDistance - distance) / maxDistance
          const attraction = force * 0.8
          star.x += dx * attraction * 0.02
          star.y += dy * attraction * 0.02
        }

        // Gentle drift when not near mouse
        star.x += Math.sin(Date.now() * 0.0001 + star.z * 0.01) * 0.1
        star.y += Math.cos(Date.now() * 0.0001 + star.z * 0.01) * 0.05

        // Keep stars in bounds with wrap-around
        if (star.x < 0) star.x = canvas.width
        if (star.x > canvas.width) star.x = 0
        if (star.y < 0) star.y = canvas.height
        if (star.y > canvas.height) star.y = 0

        // Twinkle effect
        const twinkle = Math.sin(Date.now() * 0.003 + star.z) * 0.3 + 0.7
        const finalOpacity = star.opacity * twinkle

        // Glow effect when near mouse
        let glowSize = star.size
        let glowOpacity = finalOpacity
        if (distance < maxDistance) {
          const glowIntensity = (maxDistance - distance) / maxDistance
          glowSize = star.size * (1 + glowIntensity * 2)
          glowOpacity = Math.min(1, finalOpacity + glowIntensity * 0.5)
        }

        // Draw star with glow
        const starGradient = ctx.createRadialGradient(
          star.x, star.y, 0,
          star.x, star.y, glowSize * 3
        )
        starGradient.addColorStop(0, star.color + glowOpacity + ')')
        starGradient.addColorStop(0.3, star.color + (glowOpacity * 0.5) + ')')
        starGradient.addColorStop(1, star.color + '0)')

        ctx.beginPath()
        ctx.arc(star.x, star.y, glowSize * 3, 0, Math.PI * 2)
        ctx.fillStyle = starGradient
        ctx.fill()

        // Draw star core
        ctx.beginPath()
        ctx.arc(star.x, star.y, glowSize * 0.5, 0, Math.PI * 2)
        ctx.fillStyle = star.color + glowOpacity + ')'
        ctx.fill()
      })

      // Draw subtle connecting lines between nearby stars near mouse
      if (mouseX && mouseY) {
        starsRef.current.forEach((star1, i) => {
          const d1 = Math.sqrt(Math.pow(mouseX - star1.x, 2) + Math.pow(mouseY - star1.y, 2))
          if (d1 < 200) {
            starsRef.current.slice(i + 1).forEach((star2) => {
              const d2 = Math.sqrt(Math.pow(mouseX - star2.x, 2) + Math.pow(mouseY - star2.y, 2))
              if (d2 < 200) {
                const starDist = Math.sqrt(Math.pow(star1.x - star2.x, 2) + Math.pow(star1.y - star2.y, 2))
                if (starDist < 100) {
                  const lineOpacity = (1 - starDist / 100) * 0.15
                  ctx.beginPath()
                  ctx.moveTo(star1.x, star1.y)
                  ctx.lineTo(star2.x, star2.y)
                  ctx.strokeStyle = `rgba(100, 180, 255, ${lineOpacity})`
                  ctx.lineWidth = 0.5
                  ctx.stroke()
                }
              }
            })
          }
        })
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', handleMouseMove)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  )
}
